// Machine-generated prefill: describe a form's answerable fields to an external
// filler (an LLM reading an uploaded document, say), then apply what comes back
// without trusting any of it.
//
// Two halves, and the split is the point:
//
//   formFieldCatalog  — what to ASK for. A flat, JSON-serializable list of every
//                       field a document could plausibly answer, with its path,
//                       type, and legal option values.
//   applyPrefillValues — what to ACCEPT. Every incoming entry is resolved against
//                       the real definition, coerced to the field's type, written,
//                       and then validated by the renderer's own engine. Anything
//                       that fails any of those steps is rejected rather than
//                       written, so a bad guess leaves the field blank instead of
//                       poisoning the value tree.
//
// Pure — no React, no store.

import { collectFieldStates, type FieldState } from './introspection'
import { captionText } from './caption'
import { ANSWERABLE_FIELD_TYPES, isEditableField, isEmptyValue } from './unansweredFields'
import {
  type DynamicFormDefinition,
  type DynamicFormField,
  type DynamicFormValue,
  type Option,
  type NumberConfig,
  type DateConfig,
  type ScalarListConfig,
  type VisibilityRule,
  ROW_KEY,
  isGroupField,
  isRepeatableGroup,
} from './types'
import { inlineOptions, valuesEqual } from './fields/optionUtils'
import { validateFieldAtPath } from './validation'
import { labelToText } from './richText'
import {
  asObject,
  createDefaultValue,
  getFieldValue,
  newGroupRow,
  parsePath,
  setFieldValue,
} from './value'

/** Types that hold more than one answer, so the filler sends `values`. */
const MULTI_VALUE_TYPES: ReadonlySet<string> = new Set([
  'multi_select',
  'multi_checkbox',
  'scalar_list',
])

/**
 * Answerable types minus `upload` — a document's text cannot produce a file, so
 * offering upload fields to the filler only invites a fabricated answer. They
 * still count against {@link formCoverage}: the user does have to attach them.
 */
const PREFILLABLE_FIELD_TYPES: ReadonlySet<string> = new Set(
  [...ANSWERABLE_FIELD_TYPES].filter((type) => type !== 'upload'),
)

// --- Catalog ----------------------------------------------------------------

export interface CatalogOption {
  value: unknown
  label: string
}

/** One field, described for an external filler. JSON-serializable throughout. */
export interface CatalogField {
  /** Write this back verbatim as the entry's `path`. */
  path: string
  label: string
  type: string
  required: boolean
  /** True for the types that take `values` (an array) rather than `value`. */
  multiple?: boolean
  /** `help_text` / `description`, whichever the field carries. */
  help?: string
  /** Legal choices. Absent when the field is free-text or sources its options
   *  remotely — a remote-sourced enum has no answer set to offer. */
  options?: CatalogOption[]
}

/**
 * Every field a document could answer, in definition order, including fields
 * currently gated behind a visibility rule.
 *
 * Deliberately says NOTHING about that gating. An earlier version annotated
 * gated fields with the condition that reveals them, and an LLM filler read
 * that as "unanswerable" and skipped them — reporting back that "the gating
 * logic is preventing them from being filled". The filler's job is to read a
 * document, not to reason about form mechanics: it answers what it can, and
 * {@link applyPrefillValues} opens whatever conditions are needed to land the
 * answer.
 *
 * Repeatable groups are expanded one row deep (`rows[0].child`) even when they
 * start empty, so the filler has a path to write the first row to.
 *
 * Every branch of an either/or form is offered, including arms that contradict
 * each other. Custom Antibodies asks for the antibody type twice — once behind
 * "no supplier identified", once behind "yes, and compare" — and the filler
 * should answer both: whichever arm is not live gets staged by
 * {@link applyPrefillValues}, so flipping the conditional finds it already
 * filled in. Scoping the list to the live arm was worse, because it made the
 * filler's answers depend on guessing which arm that was.
 */
export function formFieldCatalog(definition: DynamicFormDefinition): CatalogField[] {
  return prefillableFieldStates(definition)
    .map((state) => {
      const options = inlineOptions(state.field)
      const helpSource = state.field.help_text ?? state.field.description
      // The catalog is read by a machine filler, not rendered — captions that
      // carry HTML are flattened to text (state.label already is).
      const help = helpSource == null ? helpSource : captionText(state.field, helpSource)
      return {
        path: state.path,
        label: state.label ?? state.key,
        type: state.type,
        required: state.required,
        ...(MULTI_VALUE_TYPES.has(state.type) ? { multiple: true } : {}),
        ...(help ? { help } : {}),
        ...(options
          ? { options: options.filter((o) => o.active !== false).map(toCatalogOption) }
          : {}),
      }
    })
}

/**
 * The fields a document may answer, as {@link FieldState}s. Both halves of this
 * module read from here and they have to agree: `applyPrefillValues` refuses
 * anything `formFieldCatalog` did not offer, so a predicate that drifted between
 * them would reject good answers as "no such field on this form".
 */
function prefillableFieldStates(definition: DynamicFormDefinition): FieldState[] {
  return collectFieldStates(definition, probeValue(definition)).filter(
    (state) => PREFILLABLE_FIELD_TYPES.has(state.type) && isEditableField(state.field),
  )
}

function toCatalogOption(option: Option): CatalogOption {
  return { value: option.value, label: labelToText(option.label) }
}

/**
 * A value tree that gives every repeatable group at least one row, so
 * `collectFieldStates` expands row children into the catalog. Never handed to
 * the store — it exists only so the walk has rows to descend into.
 */
function probeValue(definition: DynamicFormDefinition): DynamicFormValue {
  return withOneRowEach(definition.fields, createDefaultValue(definition))
}

function withOneRowEach(
  fields: DynamicFormField[],
  valueObj: DynamicFormValue,
): DynamicFormValue {
  const next: DynamicFormValue = { ...valueObj }
  for (const field of fields) {
    if (!field.key || !isGroupField(field)) continue
    const children = field.children ?? []
    if (isRepeatableGroup(field)) {
      const existing = Array.isArray(next[field.key]) ? (next[field.key] as unknown[]) : []
      const rows = existing.length > 0 ? existing : [newGroupRow(field)]
      next[field.key] = rows.map((row) => withOneRowEach(children, asObject(row)))
    } else {
      next[field.key] = withOneRowEach(children, asObject(next[field.key]))
    }
  }
  return next
}

// --- Apply ------------------------------------------------------------------

/** One proposed answer. `values` wins over `value` for multi-valued fields. */
export interface PrefillEntry {
  path: string
  value?: unknown
  values?: unknown[]
}

export interface PrefillRejection {
  path: string
  reason: string
}

export interface PrefillOutcome {
  /** The new value tree. Identical to the input when nothing was applied. */
  value: DynamicFormValue
  /** Paths actually written, in the order they were applied. Includes any gate
   *  answers from {@link gatesOpened} — they are real answers on the form and
   *  need flagging for review like any other. */
  applied: string[]
  /** Gate answers written solely to reveal a field that had data for it. */
  gatesOpened: string[]
  /** Paths written but not currently on screen, because a conditional points the
   *  other way. Answers held ready for the branch the user has not taken. */
  staged: string[]
  /** Everything refused, with why — surface these rather than swallowing them. */
  rejected: PrefillRejection[]
}

/**
 * Apply externally-proposed answers to a form value. An entry is written only if
 * all of these hold: its path names a prefillable field, that field is still
 * empty, its value coerces to the field's type, the field ends up VISIBLE, and
 * the renderer's own validator accepts the result. Existing answers are never
 * overwritten.
 *
 * Conditional gates get two different treatments, and the split is deliberate.
 *
 * When a hidden field's gates CAN be satisfied, {@link openGates} writes the gate
 * chain that reveals it, using the exact value each rule demands — nothing is
 * invented, and a gate is only ever opened because an answer is waiting behind
 * it. Those land in `applied`.
 *
 * When they cannot — the conditional points the other way, so revealing the
 * field would mean overwriting an answer — the value is STAGED: written, but
 * reported in `staged` rather than `applied`. Forms routinely ask the same thing
 * under opposite conditions, and the arm that is not live is the one the user may
 * yet switch to, so its answers wait there rather than being thrown away. They
 * stay out of `applied` because nothing off screen can carry a review pill.
 */
export function applyPrefillValues(
  definition: DynamicFormDefinition,
  value: DynamicFormValue | null | undefined,
  entries: readonly PrefillEntry[],
): PrefillOutcome {
  const catalog = new Map(
    prefillableFieldStates(definition).map((state) => [
      normalizeRowIndices(state.path),
      state,
    ]),
  )

  let working: DynamicFormValue = { ...(value ?? {}) }
  const applied: string[] = []
  const gatesOpened: string[] = []
  const staged: string[] = []
  const rejected: PrefillRejection[] = []
  // `rowMap` maps a proposed row (`donors[2]`) to the row actually written, so
  // `donors[2].age` and `donors[2].sex` land together even when index 2 does not
  // exist yet. `rowCounters` allocates the next free index per group path, so a
  // nested group's rows count separately from its parent's.
  const rowMap = new Map<string, number>()
  const rowCounters = new Map<string, number>()

  for (const entry of entries) {
    const path = typeof entry.path === 'string' ? entry.path.trim() : ''
    if (!path) {
      rejected.push({ path: String(entry.path), reason: 'no field path given' })
      continue
    }
    const state = catalog.get(normalizeRowIndices(path))
    if (!state) {
      rejected.push({ path, reason: 'no such field on this form' })
      continue
    }

    const writePath = resolveRowIndices(path, working, rowMap, rowCounters)
    const coerced = coerceForField(state, entry)
    if (!coerced.ok) {
      rejected.push({ path, reason: coerced.reason })
      continue
    }

    const existing = getFieldValue(working, writePath)
    if (!isEmptyValue(existing)) {
      // Writing the value that is already there is a no-op, not a refusal —
      // it happens whenever a gate write and a proposed answer agree, and
      // reporting it would read as the form having rejected something.
      if (!sameAnswer(existing, coerced.value)) {
        rejected.push({ path, reason: 'already answered' })
      }
      continue
    }

    // Runs against a candidate tree so a rejection discards the gate writes too,
    // rather than leaving the form half-opened for a value that never landed.
    const revealed = openGates(definition, working, writePath)
    if (!revealed.ok && revealed.hard) {
      rejected.push({ path, reason: revealed.reason })
      continue
    }
    const onScreen = revealed.ok
    const gates = onScreen ? revealed.gates : []

    const candidate = setFieldValue(
      onScreen ? revealed.value : working,
      writePath,
      coerced.value,
    )
    const invalid = [...gates, writePath]
      .map((at) => Object.values(validateFieldAtPath(definition, candidate, at)).flat()[0])
      .find(Boolean)
    if (invalid) {
      rejected.push({ path, reason: invalid.message })
      continue
    }

    working = candidate
    for (const gate of gates) {
      if (!applied.includes(gate)) {
        applied.push(gate)
        gatesOpened.push(gate)
      }
    }
    if (onScreen) applied.push(writePath)
    else staged.push(writePath)
  }

  return { value: working, applied, gatesOpened, staged, rejected }
}

// --- Conditional gates ------------------------------------------------------

/** How deep a chain of gates we are willing to open to reveal one field. */
const MAX_GATE_DEPTH = 8

type GateResult =
  | { ok: true; value: DynamicFormValue; gates: string[] }
  /** `hard` means never show this field to a user — refuse outright. Otherwise
   *  it is merely not reachable from the current answers, and the value can be
   *  staged for the branch the user might switch to. */
  | { ok: false; reason: string; hard: boolean }

/**
 * Make `targetPath` visible, writing as few gate answers as it takes. Each write
 * is the value the blocking rule itself names (`equals "Yes"` -> `"Yes"`), so a
 * gate is never guessed at; a rule whose satisfying value cannot be derived — a
 * bare `present`, or a negative rule on a field with no option list — blocks
 * instead. A gate the user has already answered is never overwritten: if their
 * answer is the one keeping the field hidden, that is a real disagreement with
 * the proposal and the entry is refused.
 */
function openGates(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
  targetPath: string,
): GateResult {
  let working = value
  const gates: string[] = []

  for (let step = 0; step <= MAX_GATE_DEPTH; step += 1) {
    const states = new Map(
      collectFieldStates(definition, working).map((state) => [state.path, state]),
    )
    // The gates count as much as the target does: opening one can reveal the
    // fields that depend on it while leaving the question itself behind its own
    // gate, which shows the user prefilled answers and not the choice that
    // produced them. Keep going until every write is on screen.
    const mustBeVisible = [targetPath, ...gates].filter((path) => {
      const state = states.get(path)
      // Not in the tree yet (a repeatable row that does not exist): the write
      // itself creates it, and there is no visibility to resolve until it does.
      return state != null && !state.visible
    })
    if (mustBeVisible.length === 0) return { ok: true, value: working, gates }

    let progressed = false
    for (const hiddenPath of mustBeVisible) {
      for (const nodePath of selfAndAncestors(hiddenPath)) {
        const node = states.get(nodePath)
        if (!node) continue
        if (node.field.hidden === true) {
          return { ok: false, reason: 'field is hidden on this form', hard: true }
        }
        for (const rule of node.field.visibility ?? []) {
          const sourcePath = resolveSourcePath(states, nodePath, rule.source_field_key)
          if (!sourcePath) continue
          if (!isEmptyValue(getFieldValue(working, sourcePath))) continue
          const wanted = satisfyingValue(rule, states.get(sourcePath)!.field)
          if (wanted === undefined) continue
          working = setFieldValue(working, sourcePath, wanted)
          if (!gates.includes(sourcePath)) gates.push(sourcePath)
          progressed = true
        }
      }
    }
    if (!progressed) {
      return {
        ok: false,
        reason: 'a conditional on this form currently points away from this field',
        hard: false,
      }
    }
  }
  return { ok: false, reason: 'too many conditions to reveal this field', hard: false }
}

/** `a.b[0].c` -> [`a.b[0].c`, `a.b[0]`, `a.b`, `a`]. */
function selfAndAncestors(path: string): string[] {
  const chain = [path]
  let current = path
  while (true) {
    const cut = Math.max(current.lastIndexOf('.'), current.lastIndexOf('['))
    if (cut <= 0) break
    current = current.slice(0, cut)
    chain.push(current)
  }
  return chain
}

/**
 * A visibility rule's `source_field_key` resolves against the field's own group
 * and then outward to the root (mirroring `resolveSourceField`), so try the
 * sibling scope first and widen from there.
 */
function resolveSourcePath(
  states: Map<string, FieldState>,
  nodePath: string,
  sourceKey: string,
): string | undefined {
  const scopes = selfAndAncestors(nodePath)
    .slice(1)
    .map((ancestor) => `${ancestor}.${sourceKey}`)
  for (const candidate of [...scopes, sourceKey]) {
    if (states.has(candidate)) return candidate
  }
  return undefined
}

/** The value that satisfies `rule`, or undefined when it cannot be derived. */
function satisfyingValue(rule: VisibilityRule, source: DynamicFormField): unknown {
  const choices = inlineOptions(source)?.map((option) => option.value) ?? []
  switch (rule.operator) {
    case 'equals':
      return rule.value
    case 'in':
      return Array.isArray(rule.value) && rule.value.length > 0 ? rule.value[0] : undefined
    case 'not_equals':
      return choices.find((choice) => !valuesEqual(choice, rule.value))
    case 'not_in': {
      const excluded = Array.isArray(rule.value) ? rule.value : []
      return choices.find((choice) => !excluded.some((bad) => valuesEqual(bad, choice)))
    }
    // `present` names no value to write, and a rule pairing it with an `equals`
    // (the common shape) is satisfied by that `equals` instead.
    default:
      return undefined
  }
}

/** Whether a proposed answer is indistinguishable from the one already stored. */
function sameAnswer(existing: unknown, proposed: unknown): boolean {
  if (Array.isArray(existing) || Array.isArray(proposed)) {
    const a = Array.isArray(existing) ? existing : [existing]
    const b = Array.isArray(proposed) ? proposed : [proposed]
    return a.length === b.length && a.every((item, i) => valuesEqual(item, b[i]))
  }
  return valuesEqual(existing, proposed)
}

/** `contacts[3].email` -> `contacts[0].email`, for schema lookups. */
function normalizeRowIndices(path: string): string {
  return path.replace(/\[\d+\]/g, '[0]')
}

/**
 * Rewrite each row index to the slot it should actually occupy: the first row
 * that carries no answers yet, then the ones after it. Keeps the filler's rows
 * contiguous (no array holes) and stops a proposed `rows[0]` from landing past
 * a seeded-but-empty first row.
 */
function resolveRowIndices(
  path: string,
  value: DynamicFormValue,
  rowMap: Map<string, number>,
  rowCounters: Map<string, number>,
): string {
  let resolved = ''
  for (const segment of parsePath(path)) {
    if (typeof segment === 'number') {
      const key = `${resolved}[${segment}]`
      let index = rowMap.get(key)
      if (index === undefined) {
        index = rowCounters.get(resolved) ?? firstFreeRowIndex(value, resolved)
        rowCounters.set(resolved, index + 1)
        rowMap.set(key, index)
      }
      resolved += `[${index}]`
    } else {
      resolved += resolved === '' ? segment : `.${segment}`
    }
  }
  return resolved
}

/** The first row of `groupPath` carrying no answers — a seeded-but-empty row
 *  gets filled rather than skipped — or one past the end when all are in use. */
function firstFreeRowIndex(value: DynamicFormValue, groupPath: string): number {
  const rows = getFieldValue(value, groupPath)
  const existing = Array.isArray(rows) ? rows : []
  const firstBlank = existing.findIndex((row) => isBlankRow(row))
  return firstBlank === -1 ? existing.length : firstBlank
}

/** A row nobody has answered anything in — the internal `__key` doesn't count. */
function isBlankRow(row: unknown): boolean {
  if (row == null || typeof row !== 'object') return true
  return Object.entries(row as Record<string, unknown>).every(
    ([key, cell]) => key === ROW_KEY || isBlankCell(cell),
  )
}

function isBlankCell(cell: unknown): boolean {
  if (cell != null && typeof cell === 'object' && !Array.isArray(cell)) return isBlankRow(cell)
  return isEmptyValue(cell)
}

// --- Coercion ---------------------------------------------------------------

type Coerced = { ok: true; value: unknown } | { ok: false; reason: string }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?/

function coerceForField(state: FieldState, entry: PrefillEntry): Coerced {
  const raw = entry.values !== undefined ? entry.values : entry.value
  if (raw === undefined || raw === null || raw === '') {
    return { ok: false, reason: 'no value given' }
  }

  if (MULTI_VALUE_TYPES.has(state.type)) return coerceMulti(state, raw)

  const single = Array.isArray(raw) ? raw[0] : raw
  if (single === undefined || single === null || single === '') {
    return { ok: false, reason: 'no value given' }
  }

  switch (state.type) {
    case 'text':
    case 'textarea': {
      const text = String(single).trim()
      return text === '' ? { ok: false, reason: 'blank text' } : { ok: true, value: text }
    }
    case 'number':
      return coerceNumber(state.field, single)
    case 'boolean':
      return coerceBoolean(single)
    case 'date':
      return coerceDate(state.field, single)
    case 'select':
    case 'radio':
      return coerceOption(state.field, single)
    default:
      return { ok: false, reason: `${state.type} fields can't be prefilled` }
  }
}

function coerceMulti(state: FieldState, raw: unknown): Coerced {
  const items = Array.isArray(raw) ? raw : [raw]
  const kept: unknown[] = []
  for (const item of items) {
    if (item === undefined || item === null || item === '') continue
    const coerced =
      state.type === 'scalar_list'
        ? coerceScalarItem(state.field, item)
        : coerceOption(state.field, item)
    if (coerced.ok) kept.push(coerced.value)
  }
  if (kept.length === 0) {
    return {
      ok: false,
      reason:
        state.type === 'scalar_list' ? 'no usable list items' : 'no value matched an option',
    }
  }
  const max = (state.field.config as { max_items?: number } | undefined)?.max_items
  if (typeof max === 'number' && kept.length > max) {
    return { ok: false, reason: `more than ${max} values` }
  }
  return { ok: true, value: kept }
}

function coerceScalarItem(field: DynamicFormField, item: unknown): Coerced {
  const itemType = (field.config as ScalarListConfig | undefined)?.item_type ?? 'string'
  switch (itemType) {
    case 'integer':
    case 'number': {
      const numeric = coerceNumber(field, item)
      if (!numeric.ok) return numeric
      if (itemType === 'integer' && !Number.isInteger(numeric.value)) {
        return { ok: false, reason: 'not a whole number' }
      }
      return numeric
    }
    case 'boolean':
      return coerceBoolean(item)
    default: {
      const text = String(item).trim()
      return text === '' ? { ok: false, reason: 'blank list item' } : { ok: true, value: text }
    }
  }
}

function coerceNumber(field: DynamicFormField, raw: unknown): Coerced {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { ok: true, value: raw } : { ok: false, reason: 'not a number' }
  }
  // Thousands separators and stray currency/unit text are common in quote PDFs.
  const cleaned = String(raw).replace(/,/g, '').trim()
  const numeric = Number(cleaned)
  if (!Number.isFinite(numeric)) return { ok: false, reason: `"${raw}" is not a number` }
  const config = field.config as NumberConfig | undefined
  if (config?.integer === true && !Number.isInteger(numeric)) {
    return { ok: false, reason: 'not a whole number' }
  }
  return { ok: true, value: numeric }
}

const TRUE_WORDS = new Set(['true', 'yes', 'y', '1'])
const FALSE_WORDS = new Set(['false', 'no', 'n', '0'])

function coerceBoolean(raw: unknown): Coerced {
  if (typeof raw === 'boolean') return { ok: true, value: raw }
  const word = String(raw).trim().toLowerCase()
  if (TRUE_WORDS.has(word)) return { ok: true, value: true }
  if (FALSE_WORDS.has(word)) return { ok: true, value: false }
  return { ok: false, reason: `"${raw}" is not a yes/no answer` }
}

function coerceDate(field: DynamicFormField, raw: unknown): Coerced {
  const text = String(raw).trim()
  const wantsTime = (field.config as DateConfig | undefined)?.variant === 'datetime'
  if (wantsTime ? ISO_DATETIME.test(text) : ISO_DATE.test(text)) {
    return { ok: true, value: text }
  }
  return { ok: false, reason: `"${text}" is not an ISO date` }
}

/**
 * Match a proposed answer to one of the field's options — by value first, then
 * by label, case- and whitespace-insensitively. A field whose options load from
 * a remote data source has no answer set here, so it is refused outright rather
 * than filled with an unverifiable string.
 */
function coerceOption(field: DynamicFormField, raw: unknown): Coerced {
  const options = inlineOptions(field)
  if (!options || options.length === 0) {
    return { ok: false, reason: 'this field loads its choices from a data source' }
  }
  const needle = String(raw).trim().toLowerCase()
  const byValue = options.find((option) => String(option.value).trim().toLowerCase() === needle)
  if (byValue) return { ok: true, value: byValue.value }
  const byLabel = options.find((option) => labelToText(option.label).toLowerCase() === needle.replace(/\s+/g, ' '))
  if (byLabel) return { ok: true, value: byLabel.value }
  return { ok: false, reason: `"${raw}" is not one of the choices` }
}
